import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { detectInitialLocale } from "@/lib/i18n";

const DE_URL = "https://omselected.art/magazin/red-flag-fragen";
const EN_URL = "https://omselected.art/magazin/red-flag-questions";

type Faq = { q: string; a: string };

const content = {
  de: {
    home: "Start",
    magazine: "Magazin",
    eyebrow: "Magazin, vor der Buchung",
    label: "Red-Flag-Fragen",
    title: "Red-Flag-Fragen vor der Buchung",
    lede: "Sechs Fragen, die dir vor jeder Buchung zeigen, ob du es mit seriösen Kreativen zu tun hast.",
    intro:
      "Bevor du Fotograf:in oder Visagist:in buchst, lohnt es sich, die richtigen Fragen zu stellen. Die Antworten verraten viel über Professionalität, Verlässlichkeit und Fairness. Bleiben Antworten vage oder ausweichend, ist das oft ein klares Warnsignal. Nutze die folgenden Fragen als Checkliste, um vor der Buchung bessere Entscheidungen zu treffen.",
    ctaHeading: "Lieber gleich kuratiert buchen?",
    ctaBody:
      "Auf OM Selected findest du handverlesene Fotograf:innen und Visagist:innen. Beschreibe dein Vorhaben in einem kurzen Briefing.",
    sendRequest: "Anfrage senden",
  },
  en: {
    home: "Home",
    magazine: "Magazine",
    eyebrow: "Magazine, before you book",
    label: "Red-flag questions",
    title: "Red-flag questions before booking",
    lede: "Six questions that reveal, before any booking, whether you're dealing with serious creatives.",
    intro:
      "Before you book a photographer or make-up artist, it pays to ask the right questions. The answers tell you a lot about professionalism, reliability and fairness. Vague or evasive answers are often a clear warning sign. Use the following questions as a checklist to make better decisions before booking.",
    ctaHeading: "Prefer to book curated right away?",
    ctaBody:
      "On OM Selected you'll find handpicked photographers and make-up artists. Describe your project in a short briefing.",
    sendRequest: "Send a request",
  },
};

export const Route = createFileRoute("/magazin/red-flag-fragen")({
  head: () => {
    const isEn = detectInitialLocale() === "en";
    const c = isEn ? content.en : content.de;
    const url = isEn ? EN_URL : DE_URL;
    const description = isEn
      ? "Red-flag questions to ask before booking a photographer or make-up artist: price, full galleries, backup plans, turnaround, rights and contracts."
      : "Red-Flag-Fragen vor der Buchung von Fotograf:in oder Visagist:in: Preis, vollständige Galerien, Backup-Plan, Lieferzeit, Rechte und Vertrag.";
    return {
      meta: [
        { title: `${c.title}, OM Selected` },
        { name: "description", content: description },
        { property: "og:title", content: `${c.title}, OM Selected` },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "de", href: DE_URL },
        { rel: "alternate", hrefLang: "en", href: EN_URL },
        { rel: "alternate", hrefLang: "x-default", href: DE_URL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: c.title,
            description,
            url,
            inLanguage: isEn ? "en" : "de",
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: "https://omselected.art/" },
              { "@type": "ListItem", position: 2, name: "Magazin", item: "https://omselected.art/magazin/" },
              { "@type": "ListItem", position: 3, name: c.label, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: RedFlagPage,
});

function RedFlagPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const c = isEn ? content.en : content.de;
  const items = t("magazine.redflagItems", { returnObjects: true }) as Faq[];

  return (
    <div className="bg-background text-foreground">
      <section className="border-b border-border px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] pt-12 md:pt-20 pb-16 md:pb-24">
          <nav className="eyebrow text-muted-foreground mb-6 flex gap-2">
            <Link to="/" className="hover:underline">{c.home}</Link>
            <span>/</span>
            <Link to="/magazin" className="hover:underline">{c.magazine}</Link>
          </nav>
          <span className="eyebrow text-muted-foreground block mb-6">{c.eyebrow}</span>
          <h1 className="serif text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
            {c.title}
          </h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-2xl leading-snug text-muted-foreground">
            {c.lede}
          </p>
        </div>
      </section>

      <section className="border-b border-border px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] py-16 md:py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-muted-foreground leading-relaxed max-w-md">{c.intro}</p>
          </div>
          <ol className="md:col-span-8 divide-y divide-border">
            {items.map((item, i) => (
              <li key={item.q} className="py-8 flex gap-6">
                <span className="serif text-3xl md:text-4xl text-muted-foreground shrink-0 leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2 className="serif text-xl md:text-2xl leading-snug">{item.q}</h2>
                  <p className="mt-3 text-muted-foreground leading-relaxed max-w-2xl">{item.a}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] py-16 md:py-24 text-center">
          <h2 className="serif text-3xl md:text-4xl leading-tight">{c.ctaHeading}</h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto leading-relaxed">{c.ctaBody}</p>
          <Link
            to="/brief"
            className="eyebrow inline-block mt-8 border border-foreground px-8 py-4 hover:bg-foreground hover:text-background transition-colors"
          >
            {c.sendRequest}
          </Link>
        </div>
      </section>
    </div>
  );
}